import { useState, useEffect } from 'react';
import api from '../../services/api';
import { MapPin, Phone, CheckCircle, PackageSearch, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useTenant } from '../../context/TenantContext';
import Loading from '../../components/Loading';

export default function DeliveryDashboard() {
    const toast = useToast();
    const { tenant } = useTenant();
    const [deliveries, setDeliveries] = useState([]);
    const [historyRides, setHistoryRides] = useState([]);
    const [activeTab, setActiveTab] = useState('active'); // 'active' or 'history'
    const [loading, setLoading] = useState(true);
    const [otpInput, setOtpInput] = useState('');
    const [activeVerification, setActiveVerification] = useState(null);
    const [isAvailable, setIsAvailable] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [expandedRideId, setExpandedRideId] = useState(null);
    const [ridesSearch, setRidesSearch] = useState('');

    useEffect(() => {
        fetchStatus();
        fetchDeliveries();

        // Auto-refresh every 30 seconds
        const interval = setInterval(() => {
            fetchStatus();
            fetchDeliveries();
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const fetchStatus = async () => {
        try {
            const res = await api.get('/api/delivery-partner/status');
            setIsAvailable(res.data.is_available);
        } catch (error) {
            console.error('Failed to fetch status', error);
        }
    };

    const fetchDeliveries = async () => {
        try {
            const [activeRes, historyRes] = await Promise.all([
                api.get('/api/delivery-partner/my-deliveries'),
                api.get('/api/delivery-partner/my-rides')
            ]);
            setDeliveries(activeRes.data);
            setHistoryRides(historyRes.data);
        } catch (error) {
            console.error('Failed to fetch deliveries', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (action, orderId, payload = {}) => {
        setActionLoading(orderId);
        try {
            await api.post(`/api/delivery-partner/${action}`, { orderId, ...payload });
            setActiveVerification(null);
            setOtpInput('');
            await fetchDeliveries();
        } catch (error) {
            toast.error(error.response?.data?.message || `Failed to perform action: ${action}`);
        } finally {
            setActionLoading(null);
        }
    };

    const handleToggleAvailability = async () => {
        try {
            const newStatus = !isAvailable;
            await api.put('/api/delivery-partner/status', { is_available: newStatus });
            setIsAvailable(newStatus);
        } catch (error) {
            toast.error('Failed to update availability');
        }
    };

    if (loading) return <Loading mode="page" message="Searching for assigned deliveries..." />;

    const primaryTabStyle = (tabName) => activeTab === tabName && tenant?.primary_color ? { borderBottomColor: tenant.primary_color, color: tenant.primary_color } : {};
    const primaryBorderStyle = tenant?.primary_color ? { borderLeftColor: tenant.primary_color } : {};

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
            {actionLoading && (
                <Loading mode="full" message="Processing action..." />
            )}
            <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Delivery Dashboard</h1>
                <div className="flex items-center space-x-3 bg-white px-4 py-3 rounded-xl shadow-sm border">
                    <span className="text-sm font-medium text-gray-700">Work Status:</span>
                    <button
                        onClick={() => {
                            const isActiveDelivery = deliveries.some(d => ['assigned', 'accepted', 'picked_up'].includes(d.delivery_status));
                            if (isActiveDelivery && isAvailable) {
                                toast.warning("You cannot go offline while you have active deliveries to complete.");
                                return;
                            }
                            handleToggleAvailability();
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isAvailable ? 'bg-green-500' : 'bg-gray-300'} ${deliveries.some(d => ['assigned', 'accepted', 'picked_up'].includes(d.delivery_status)) && isAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={deliveries.some(d => ['assigned', 'accepted', 'picked_up'].includes(d.delivery_status)) && isAvailable ? "Cannot change status during active delivery" : "Toggle Availability"}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isAvailable ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className={`text-sm font-bold w-16 ${isAvailable ? 'text-green-600' : 'text-gray-500'}`}>
                        {isAvailable ? 'Online' : 'Offline'}
                    </span>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b mb-6 mt-2">
                <button
                    onClick={() => setActiveTab('active')}
                    className={`px-6 py-3 font-semibold text-sm transition-colors ${activeTab === 'active'
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    style={primaryTabStyle('active')}
                >
                    Active Deliveries
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`px-6 py-3 font-semibold text-sm transition-colors ${activeTab === 'history'
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    style={primaryTabStyle('history')}
                >
                    My Rides ({historyRides.length})
                </button>
            </div>

            {activeTab === 'active' ? (
                deliveries.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-sm text-gray-500">
                        <PackageSearch className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                        No active deliveries assigned to you right now.
                    </div>
                ) : (
                    <div className="space-y-6">
                        {deliveries.map(delivery => (
                            <div key={delivery.id} className="card p-6 border-l-4 border-primary-500" style={primaryBorderStyle}>
                                <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-4">
                                    <div>
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                            Order #{delivery.id}
                                        </span>
                                        <h3 className="text-xl font-bold mt-1 text-gray-900">{delivery.customer_name}</h3>
                                        <div className="flex items-center text-gray-600 mt-2">
                                            <Phone className="w-4 h-4 mr-2" />
                                            <a href={`tel:${delivery.customer_phone}`} className="hover:text-primary-600 font-medium">
                                                {delivery.customer_phone}
                                            </a>
                                        </div>
                                        <div className="flex items-start text-gray-600 mt-2 max-w-sm">
                                            <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                                            <span>{delivery.delivery_address}</span>
                                        </div>
                                        {delivery.delivery_notes && (
                                            <div className="mt-2 bg-yellow-50 p-2 text-sm text-yellow-800 rounded">
                                                <span className="font-bold">Note:</span> {delivery.delivery_notes}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-left md:text-right">
                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase ${delivery.delivery_status === 'picked_up' ? 'bg-blue-100 text-blue-800' :
                                            delivery.delivery_status === 'accepted' ? 'bg-indigo-100 text-indigo-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                            {delivery.delivery_status.replace('_', ' ')}
                                        </span>
                                        <div className="mt-2 font-medium text-gray-500">
                                            Collect: <span className="text-black font-bold text-lg">
                                                {delivery.payment_status === 'paid' ? 'Paid Online' : `₹${delivery.final_total_amount}`}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <hr className="my-4 border-gray-100" />

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3">
                                    {delivery.delivery_status === 'assigned' && (
                                        <>
                                            <button
                                                onClick={() => handleAction('accept', delivery.id)}
                                                disabled={actionLoading === delivery.id}
                                                className="btn-primary w-full sm:w-auto disabled:opacity-50"
                                            >
                                                {actionLoading === delivery.id ? 'Processing...' : 'Accept Order'}
                                            </button>
                                            <button
                                                onClick={() => handleAction('reject', delivery.id)}
                                                disabled={actionLoading === delivery.id}
                                                className="px-4 py-2 border rounded-lg text-red-600 border-red-200 hover:bg-red-50 font-bold w-full sm:w-auto transition-colors disabled:opacity-50"
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}
                                    {delivery.delivery_status === 'accepted' && (
                                        <button
                                            onClick={() => handleAction('picked-up', delivery.id)}
                                            disabled={actionLoading === delivery.id}
                                            className="btn-primary bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto disabled:opacity-50"
                                        >
                                            {actionLoading === delivery.id ? 'Processing...' : 'Mark as Picked Up'}
                                        </button>
                                    )}
                                    {delivery.delivery_status === 'picked_up' && activeVerification !== delivery.id && (
                                        <button
                                            onClick={() => setActiveVerification(delivery.id)}
                                            disabled={actionLoading === delivery.id}
                                            className="btn-primary bg-green-600 hover:bg-green-700 w-full sm:w-auto disabled:opacity-50"
                                        >
                                            Complete Delivery
                                        </button>
                                    )}

                                    {/* OTP Form */}
                                    {activeVerification === delivery.id && (
                                        <div className="w-full bg-gray-50 p-4 rounded-lg flex flex-col sm:flex-row items-center gap-3">
                                            <input
                                                type="text"
                                                placeholder="Enter Customer OTP"
                                                value={otpInput}
                                                onChange={(e) => setOtpInput(e.target.value)}
                                                className="px-4 py-2 border rounded-lg w-full sm:w-64 text-center tracking-widest text-lg font-bold"
                                                maxLength={6}
                                            />
                                            <button
                                                onClick={() => handleAction('delivered', delivery.id, { otp: otpInput })}
                                                disabled={actionLoading === delivery.id}
                                                className="btn-primary bg-green-600 hover:bg-green-700 w-full sm:w-auto flex items-center justify-center whitespace-nowrap disabled:opacity-50"
                                            >
                                                <CheckCircle className="w-5 h-5 mr-2" />
                                                {actionLoading === delivery.id ? 'Verifying...' : 'Verify & Complete'}
                                            </button>
                                            <button
                                                onClick={() => { setActiveVerification(null); setOtpInput(''); }}
                                                className="btn-secondary w-full sm:w-auto"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                /* My Rides Tab */
                <div className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by customer name, order ID, or address..."
                            value={ridesSearch}
                            onChange={(e) => setRidesSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                        />
                    </div>

                    {(() => {
                        const filtered = historyRides.filter(ride => {
                            if (!ridesSearch) return true;
                            const s = ridesSearch.toLowerCase();
                            return (
                                ride.customer_name?.toLowerCase().includes(s) ||
                                String(ride.order_id)?.includes(s) ||
                                ride.delivery_address?.toLowerCase().includes(s)
                            );
                        });

                        if (filtered.length === 0) return (
                            <div className="text-center py-12 bg-white rounded-xl shadow-sm text-gray-500">
                                <PackageSearch className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                                {ridesSearch ? 'No rides match your search.' : "You haven't processed any rides yet."}
                            </div>
                        );

                        return filtered.map(ride => (
                            <div
                                key={ride.id}
                                className={`card p-6 border-l-4 cursor-pointer transition-all ${
                                    expandedRideId === ride.id
                                        ? 'border-primary-500 shadow-md'
                                        : 'border-gray-300 opacity-90 hover:opacity-100 hover:shadow-sm'
                                }`}
                                onClick={() => setExpandedRideId(expandedRideId === ride.id ? null : ride.id)}
                            >
                                <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
                                    <div>
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                            Order #{ride.order_id}
                                        </span>
                                        <h3 className="text-xl font-bold mt-1 text-gray-900">{ride.customer_name}</h3>
                                        <div className="flex items-start text-gray-600 mt-2 max-w-sm">
                                            <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                                            <span>{ride.delivery_address}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-start md:items-end gap-2">
                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase ${
                                            ride.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                            ride.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {ride.status}
                                        </span>
                                        <div className="text-xs text-gray-500">
                                            {new Date(ride.updated_at).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                        <div className="text-gray-400">
                                            {expandedRideId === ride.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded Detail Panel */}
                                {expandedRideId === ride.id && (
                                    <div className="mt-5 pt-5 border-t border-gray-200 space-y-4" onClick={e => e.stopPropagation()}>
                                        <h4 className="font-bold text-gray-800 text-base">Full Order Details</h4>
                                        <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                                            <span className="text-gray-500">Full Order ID:</span>
                                            <span className="font-mono text-xs break-all">{ride.order_id}</span>

                                            <span className="text-gray-500">Customer:</span>
                                            <span>{ride.customer_name}</span>

                                            <span className="text-gray-500">Phone:</span>
                                            <span>{ride.customer_phone || ride.contact_phone || '-'}</span>

                                            <span className="text-gray-500">Delivery Address:</span>
                                            <span>{ride.delivery_address}</span>

                                            {ride.delivery_notes && (<>
                                                <span className="text-gray-500">Notes:</span>
                                                <span>{ride.delivery_notes}</span>
                                            </>)}

                                            <span className="text-gray-500">Order Total:</span>
                                            <span className="font-bold">₹{parseFloat(ride.final_total_amount || 0).toFixed(2)}</span>

                                            <span className="text-gray-500">Payment:</span>
                                            <span>{ride.payment_status === 'paid' ? '✅ Paid' : '⏳ Pending'}</span>
                                        </div>

                                        {/* Delivery completion info */}
                                        {ride.status === 'delivered' && (
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-1">
                                                <h5 className="font-semibold text-green-800 text-sm">✅ Delivery Completed</h5>
                                                {ride.delivered_at && (
                                                    <p className="text-sm text-green-700">Delivered at: {new Date(ride.delivered_at).toLocaleString()}</p>
                                                )}
                                                {ride.picked_up_at && (
                                                    <p className="text-sm text-green-700">Picked up at: {new Date(ride.picked_up_at).toLocaleString()}</p>
                                                )}
                                                {ride.accepted_at && (
                                                    <p className="text-sm text-green-700">Accepted at: {new Date(ride.accepted_at).toLocaleString()}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ));
                    })()}
                </div>
            )}
        </div>
    );
}
